import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Mail, AlertTriangle, CheckCircle, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import emailjs from "@emailjs/browser";
import "./LegalPages.css";

// EmailJS Configuration
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "your_service_id";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "your_template_id";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "your_public_key";

function AccountDeletion() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    reason: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send deletion request email via EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          request_type: "ACCOUNT DELETION REQUEST",
          service_interest: "Account Deletion",
          business_type: "N/A",
          message: `ACCOUNT DELETION REQUEST\n\nReason: ${formData.reason || "Not specified"}`,
          to_email: "stepnsit@gmail.com",
        },
        EMAILJS_PUBLIC_KEY
      );

      setIsSubmitted(true);
      
      toast.success("Request Submitted", {
        description: "Your account deletion request has been sent. We'll contact you within 48 hours.",
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        reason: ""
      });

    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission Failed", {
        description: "Something went wrong. Please try again or email us directly at admin@stepandsit.com",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="legal-page">
      <div className="legal-header">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>

      <div className="legal-content">
        {!isSubmitted ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#fff3cd',
                marginBottom: '24px'
              }}>
                <AlertTriangle size={40} color="#856404" />
              </div>
              <h1>Request Account Deletion</h1>
              <p style={{ fontSize: '1.1rem', color: '#666', marginTop: '16px' }}>
                We're sorry to see you go. Please fill out the form below to request account deletion.
              </p>
            </div>

            <div style={{ 
              background: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '32px'
            }}>
              <h3 style={{ color: '#856404', marginTop: 0, marginBottom: '12px', fontSize: '1.1rem' }}>
                Important Information
              </h3>
              <ul style={{ color: '#856404', marginBottom: 0, lineHeight: '1.8' }}>
                <li>Account deletion is permanent and cannot be undone</li>
                <li>All your data will be deleted within 15 days</li>
                <li>You will lose access to all bookings and history</li>
                <li>We will send a confirmation email before final deletion</li>
                <li>You have the right to withdraw this request within 7 days</li>
              </ul>
            </div>

            <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '0 auto' }}>
              <div style={{ marginBottom: '24px' }}>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  required
                  disabled={isSubmitting}
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your registered email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  required
                  disabled={isSubmitting}
                  style={{ marginTop: '8px' }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                  This must match the email address associated with your account
                </p>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  required
                  disabled={isSubmitting}
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Label htmlFor="reason">Reason for Deletion (Optional)</Label>
                <Textarea
                  id="reason"
                  placeholder="Help us improve by telling us why you're leaving..."
                  value={formData.reason}
                  onChange={(e) => handleInputChange('reason', e.target.value)}
                  rows={5}
                  disabled={isSubmitting}
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div style={{ 
                background: '#f8d7da', 
                border: '1px solid #dc3545',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <p style={{ color: '#721c24', margin: 0, fontSize: '0.95rem' }}>
                  <strong>By submitting this form, I confirm that:</strong>
                </p>
                <ul style={{ color: '#721c24', marginBottom: 0, marginTop: '8px', lineHeight: '1.6' }}>
                  <li>I understand this action is permanent</li>
                  <li>I want to delete all my personal data from Step and Sit</li>
                  <li>I agree to lose access to all services and bookings</li>
                </ul>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                disabled={isSubmitting}
                style={{ 
                  width: '100%',
                  background: '#dc3545',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Submitting Request...
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    Submit Deletion Request
                  </>
                )}
              </Button>
            </form>

            <div style={{ marginTop: '32px', textAlign: 'center', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <p style={{ margin: 0, color: '#666' }}>
                Need help or have questions?<br />
                Contact us at <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a> or call{' '}
                <a href="tel:+918848625313">+91 88486 25313</a>
              </p>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: '#d4edda',
              marginBottom: '24px'
            }}>
              <CheckCircle size={60} color="#155724" />
            </div>
            
            <h2 style={{ color: '#155724', marginBottom: '16px' }}>
              Request Received
            </h2>
            
            <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
              Your account deletion request has been submitted successfully. Our team will review your request and contact you within <strong>48 hours</strong>.
            </p>

            <div style={{ 
              background: '#d1ecf1', 
              border: '1px solid #17a2b8',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '32px',
              maxWidth: '600px',
              margin: '0 auto 32px',
              textAlign: 'left'
            }}>
              <h3 style={{ color: '#0c5460', marginTop: 0, marginBottom: '12px' }}>
                What Happens Next?
              </h3>
              <ul style={{ color: '#0c5460', marginBottom: 0, lineHeight: '1.8' }}>
                <li>We'll verify your identity via email</li>
                <li>You'll receive a confirmation link</li>
                <li>You have 7 days to withdraw your request</li>
                <li>After confirmation, deletion occurs within 15 days</li>
                <li>You'll receive final confirmation once complete</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button 
                onClick={() => navigate('/')}
                variant="outline"
              >
                Back to Home
              </Button>
              <Button 
                onClick={() => setIsSubmitted(false)}
                variant="outline"
              >
                Submit Another Request
              </Button>
            </div>

            <p style={{ marginTop: '24px', color: '#666', fontSize: '0.9rem' }}>
              Changed your mind? Contact us immediately at{' '}
              <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a>
            </p>
          </div>
        )}
      </div>

      <div className="legal-footer">
        <Button onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </div>
    </div>
  );
}

export default AccountDeletion;
